import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getNotices, deleteNotice } from '../../../models/noticesModel';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

function NoticesList() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { can, user, isCallCenter } = useAuth();
  const navigate = useNavigate();

  // Utility function to strip HTML tags and get plain text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Utility function to format areas data
  const formatAreas = (areas) => {
    if (!areas) return 'No areas';
    
    // Handle different data structures
    if (Array.isArray(areas)) {
      const areaNames = areas.map(area => {
        if (typeof area === 'string') return area;
        if (typeof area === 'object' && area !== null) {
          return area.area_name || area.name || area.title || area.area_title || 'Unknown Area';
        }
        return 'Unknown Area';
      }).filter(name => name && name !== 'Unknown Area');
      
      return areaNames.length > 0 ? areaNames.join(', ') : 'No areas';
    }
    
    // Handle single area object
    if (typeof areas === 'object' && areas !== null) {
      return areas.area_name || areas.name || areas.title || areas.area_title || 'Unknown Area';
    }
    
    // Handle string
    if (typeof areas === 'string') {
      return areas;
    }
    
    return 'No areas';
  };

  // Calculate pagination values
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch notices function
  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (searchTerm) params.filter = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const response = await getNotices(params);
      if (response.status === 1) {
        // Handle paginated response (expected format)
        if (response.data && typeof response.data === 'object' && response.data.rows && Array.isArray(response.data.rows)) {
          // Paginated response - this is the expected format from the API
          setNotices(response.data.rows);
          setTotalCount(response.data.total || response.data.totalCount || response.data.rows.length);
        } else if (Array.isArray(response.data)) {
          // Fallback: Direct array response (legacy support)
          setNotices(response.data);
          setTotalCount(response.data.length);
        } else if (Array.isArray(response)) {
          // Another fallback: Response is directly an array
          setNotices(response);
          setTotalCount(response.length);
        } else {
          // Fallback: No valid data
          setNotices([]);
          setTotalCount(0);
        }
      } else {
        console.error('Error fetching notices:', response.message);
        toast.error(response.message || 'Failed to fetch notices');
        setNotices([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
      toast.error('Failed to fetch notices');
      setNotices([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // Format date utility - handles Unix timestamps from notice_time field
  const formatDate = (dateValue) => {
    if (!dateValue) return 'No date';
    
    // Check if it's a Unix timestamp (number or numeric string)
    const timestamp = parseInt(dateValue);
    if (!isNaN(timestamp) && timestamp > 0) {
      // Convert Unix timestamp to milliseconds and create date
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
    
    // Fallback to regular date parsing
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Search handlers
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Delete handler with SweetAlert
  const handleDelete = async (notice) => {
    const result = await Swal.fire({
      title: 'Delete Notice',
      text: `Are you sure you want to delete "${notice.notice_title || 'this notice'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#B0796D',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteNotice(notice.notice_id);
        if (response.status === 1) {
          toast.success('Notice deleted successfully');
          fetchNotices();
        } else {
          throw new Error(response.message || 'Failed to delete notice');
        }
      } catch (error) {
        console.error('Error deleting notice:', error);
        toast.error('Failed to delete notice');
      }
    }
  };

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Notices</h1>
              <p className="text-xs text-gray-500 mb-1">
                Manage system notices and announcements
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                  {totalCount > 0 
                    ? `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} results`
                    : '0 results'
                  }
                  {(searchTerm || statusFilter) && (
                    <span className="ml-1" style={{ color: '#4EADE2' }}>
                      (filtered)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clear Filters Button (only show if filters are active) */}
              {(searchTerm || statusFilter) && (
                <button
                  onClick={handleClearFilters}
                  className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800 underline"
                >
                  Clear Filters
                </button>
              )}

              {/* All Notices Dropdown */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded px-3 py-1 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Notices</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd" />
                  </svg>
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={fetchNotices}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                title="Refresh"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              {/* Create Button */}
              {can('Create Notice') && (
                <Link
                  to="create"
                  className="inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#0087D4' }}
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Notice
                </Link>
              )}
            </div>

            {/* Row 2: Search Bar on Right */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-64 pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#0087D4' }}>
                Notice
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#0087D4' }}>
                Areas
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#0087D4' }}>
                Created
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#0087D4' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan="4" className="px-3 py-8 text-center">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0087D4]"></div>
                    <span className="mt-2 text-sm text-gray-500">Loading notices...</span>
                  </div>
                </td>
              </tr>
            )}
            
            {!loading && notices.length === 0 && (
              <tr>
                <td colSpan="4" className="px-3 py-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2">No notices found</p>
                </td>
              </tr>
            )}
            
            {!loading && notices.map((notice) => (
              <tr key={notice.notice_id} className="hover:bg-blue-50">
                <td className="px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-900">{notice.notice_title || 'Untitled'}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {notice.notice_description ? 
                        (stripHtmlTags(notice.notice_description).substring(0, 80) + '...') : 
                        'No description'
                      }
                    </p>
                  </div>
                </td>
                <td className="px-3 py-2 text-sm">
                  {formatAreas(notice.areas)}
                </td>
                <td className="px-3 py-2 text-sm">{formatDate(notice.notice_time || notice.created_at || notice.notice_date)}</td>
                <td className="px-3 py-2 text-sm">
                  <div className="flex space-x-2">
                    <Link
                      to={`${notice.notice_id}`}
                      className="px-2 py-1 text-white rounded text-xs font-medium hover:opacity-90 transition-all duration-200"
                      style={{ 
                        backgroundColor: '#0087D4',
                        boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)'
                      }}
                    >
                      View
                    </Link>
                    {can('Update Notice') && isCallCenter() && (
                      <Link
                        to={`${notice.notice_id}/edit`}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded text-xs font-medium transition-colors duration-200"
                      >
                        Edit
                      </Link>
                    )}
                    {can('Delete Notice') && isCallCenter() && (
                      <button
                        type="button"
                        className="px-2 py-1 text-white rounded text-xs font-medium hover:opacity-90 transition-all duration-200"
                        style={{ 
                          backgroundColor: '#B0796D',
                          boxShadow: '0 1px 2px rgba(176, 121, 109, 0.3)'
                        }}
                        onClick={() => handleDelete(notice)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {!loading && notices.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow border">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#0087D4] focus:border-[#0087D4]"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              
              {totalPages > 1 && (
                <nav className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else {
                      const start = Math.max(1, currentPage - 2);
                      const end = Math.min(totalPages, start + 4);
                      pageNum = start + i;
                      if (pageNum > end) return null;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-2 py-1 text-sm border rounded ${
                          currentPage === pageNum
                            ? 'border-[#0087D4] bg-[#0087D4] text-white'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NoticesList; 